/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SEARCH_API_DEBUG_CLEANUP
 * Date:         2025-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Debug logging removed after successful fix verification.
 *
 * File Size Check:
 * - Current: 141 lines
 * - Decision: Proceed (under 150 line limit)
 */

import { useCallback } from "react";
import { getGlobalInventoryItems, getPublicInventorySearch } from "../../../services/inventoryService";
import { getGlobalBroadcasts, getPublicBroadcastsSearch } from "../../../services/broadcastService";
import { searchCompanies } from "../../../services/companiesService";
import { SearchState } from "../types";
import { flattenBroadcasts } from "../utils/broadcastHelpers";
// [2026-07-06] GUEST_ACCESS: guests search Inventory + Broadcasts via masked /public endpoints; Companies locked (BR-03)
import { useIsGuest } from "../../../contexts/GuestModeContext";

export const useSearchAPIs = (setState: React.Dispatch<React.SetStateAction<SearchState>>) => {
  const isGuest = useIsGuest();
  const fetchInventory = useCallback(async (debouncedQ: string, sort: string) => {
    setState((prev) => ({ ...prev, inventoryLoading: true, inventoryError: null }));
    try {
      const params = { q: debouncedQ || undefined, page: 1, limit: 200, sort: sort as any };
      const response = isGuest
        ? await getPublicInventorySearch(params)
        : await getGlobalInventoryItems(params);

      // [2026-07-06] GUEST_ACCESS_PHASE2: surface the guest soft wall (BR-13)
      const respData = response.data as typeof response.data & { signupRequired?: boolean; signupMessage?: string };
      setState((prev) => ({
        ...prev,
        allInventoryItems: response.data.items,
        inventoryLoading: false,
        signupRequired: !!respData.signupRequired,
        signupMessage: respData.signupMessage || "",
      }));
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        inventoryLoading: false,
        inventoryError: error.response?.data?.message || "Failed"
      }));
    }
  }, [setState, isGuest]);

  const fetchBroadcasts = useCallback(async (debouncedQ: string, sort: string) => {
    setState((prev) => ({ ...prev, broadcastLoading: true, broadcastError: null }));
    try {
      const params = {
        q: debouncedQ || undefined,
        page: 1,
        limit: 200,
        sort: (sort.startsWith("price") ? "newest" : sort) as any
      };
      // [2026-07-06] GUEST_ACCESS: guests hit the masked /public broadcasts search (BR-09/BR-12).
      const response = isGuest
        ? await getPublicBroadcastsSearch(params)
        : await getGlobalBroadcasts(params);

      const allFlattened = flattenBroadcasts(response.data.items);

      const sortOrder = sort === "oldest" ? 1 : -1;
      allFlattened.sort((a, b) => (new Date(a.broadcastGroup?.createdAt || 0).getTime() - new Date(b.broadcastGroup?.createdAt || 0).getTime()) * sortOrder);

      // [2026-07-06] GUEST_ACCESS_PHASE2: broadcasts search shares the soft wall (BR-13)
      const bd = response.data as typeof response.data & { signupRequired?: boolean; signupMessage?: string };
      setState((prev) => ({
        ...prev,
        allBroadcastItems: allFlattened,
        broadcastLoading: false,
        ...(isGuest ? { signupRequired: !!bd.signupRequired, signupMessage: bd.signupMessage || "" } : {}),
      }));
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        broadcastLoading: false,
        broadcastError: error.response?.data?.message || "Failed"
      }));
    }
  }, [setState, isGuest]);

  const fetchCompanies = useCallback(async (debouncedQ: string, page: number, perPage: number) => {
    // [2026-07-05] GUEST_ACCESS_PHASE1: Companies tab is locked for guests (BR-03) — no fetch
    if (isGuest) {
      setState((prev) => ({ ...prev, allCompanyItems: [], companyLoading: false }));
      return;
    }
    setState((prev) => ({ ...prev, companyLoading: true, companyError: null }));
    try {
      const response = await searchCompanies({ q: debouncedQ || undefined, page, limit: perPage });
      setState((prev) => ({ ...prev, allCompanyItems: response.data.items, companyTotal: response.data.total, companyPages: response.data.pages, companyPage: response.data.page, companyLoading: false }));
    } catch (error: any) {
      setState((prev) => ({ ...prev, companyLoading: false, companyError: error.response?.data?.message || "Failed" }));
    }
  }, [setState, isGuest]);

  return { fetchInventory, fetchBroadcasts, fetchCompanies };
};

